import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface SettingsModalContextType {
  isOpen: boolean;
  initialSection: string;
  initialMonitorId: string | null;
  openSettings: (section?: string, monitorId?: string) => void;
  closeSettings: () => void;
  clearInitialMonitorId: () => void;
  onMonitorChange: (callback: () => void) => () => void;
  notifyMonitorChange: () => void;
}

const SettingsModalContext = createContext<SettingsModalContextType | undefined>(undefined);

export const SettingsModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialSection, setInitialSection] = useState('account');
  const [initialMonitorId, setInitialMonitorId] = useState<string | null>(null);

  const monitorChangeCallbacks = useRef<Set<() => void>>(new Set());

  const openSettings = useCallback((section: string = 'account', monitorId?: string) => {
    setInitialSection(section);
    setInitialMonitorId(monitorId || null);
    setIsOpen(true);

    const params = new URLSearchParams(window.location.search);
    params.set('settings', section);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  const closeSettings = useCallback(() => {
    setIsOpen(false);
    setInitialMonitorId(null);

    const params = new URLSearchParams(window.location.search);
    params.delete('settings');
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, []);

  const clearInitialMonitorId = useCallback(() => {
    setInitialMonitorId(null);
  }, []);

  const onMonitorChange = useCallback((callback: () => void) => {
    monitorChangeCallbacks.current.add(callback);
    return () => {
      monitorChangeCallbacks.current.delete(callback);
    };
  }, []);

  const notifyMonitorChange = useCallback(() => {
    monitorChangeCallbacks.current.forEach(callback => callback());
  }, []);

  return (
    <SettingsModalContext.Provider value={{
      isOpen,
      initialSection,
      initialMonitorId,
      openSettings,
      closeSettings,
      clearInitialMonitorId,
      onMonitorChange,
      notifyMonitorChange
    }}>
      {children}
    </SettingsModalContext.Provider>
  );
};

export const useSettingsModal = () => {
  const context = useContext(SettingsModalContext);
  if (!context) {
    throw new Error('useSettingsModal must be used within a SettingsModalProvider');
  }
  return context;
};
